/**
 * Cryptographic Utilities
 * 
 * Provides cryptographic functions for secure random generation,
 * hashing, and encoding operations.
 */

/**
 * Generate a cryptographically random string
 * 
 * @param length - The desired length of the random string
 * @param charset - Optional custom character set (defaults to alphanumeric)
 * @returns A random string of the specified length
 */
export function generateRandomString(
  length: number,
  charset: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
): string {
  const randomValues = new Uint8Array(length);
  const cryptoObj = globalThis.crypto || crypto;
  cryptoObj.getRandomValues(randomValues);
  
  return Array.from(randomValues)
    .map(value => charset[value % charset.length])
    .join('');
}

/**
 * Base64 URL encode a buffer
 * 
 * Converts an ArrayBuffer to a base64url-encoded string.
 * Base64url encoding is URL-safe (no +, /, or = characters).
 * 
 * @param buffer - The buffer to encode
 * @returns Base64url-encoded string
 */
export function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * SHA-256 hash a string
 * 
 * @param input - The string to hash
 * @returns A promise that resolves to the SHA-256 hash as an ArrayBuffer
 */
export async function sha256(input: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  
  // Use globalThis.crypto to ensure we get the polyfilled version in tests
  const cryptoObj = globalThis.crypto || crypto;
  return await cryptoObj.subtle.digest('SHA-256', data);
}

/**
 * Generate a random state parameter for OAuth/CSRF protection
 * 
 * The state parameter should be:
 * - Cryptographically random
 * - At least 32 characters long
 * - URL-safe
 * 
 * @returns A random state string
 */
export function generateState(): string {
  return generateRandomString(32);
}
